import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  inject,
} from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import {
  faEdit,
  faBullhorn,
  faFileLines,
} from "@fortawesome/free-solid-svg-icons";
import { RouterModule } from "@angular/router";
import { StudentProfile } from "../../../api/models/student-profile";
import {
  ProfileTypeUtils,
  ProfileLookingForUtils,
} from "../../../shared/utils/student-profile.utils";
import { APP_CONFIG } from "src/assets/config/app-config";
import { SkilldService } from "../../../api/services/skilld.service";
import { ModalService } from "../../../shared/services/modal.service";
import { AlertService } from "../../../shared/services/alert.service";
import { ModalStudentProfileStatusComponent } from "../../../student/student-profile/modal-student-profile-status/modal-student-profile-status.component";
import { StudentAnnouncementService } from "../../../shared/services/student-announcement.service";
import { signal } from "@angular/core";
import { DestroyRef } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
  selector: "app-student-top-panel",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, FontAwesomeModule, RouterModule],
  template: `
    <div class="entity-details editable-group">
      <div class="edit-icon" (click)="editStatus()">
        <fa-icon [icon]="faEdit"></fa-icon>
      </div>
      <p>
        {{ "I am a" | translate }}:
        {{
          entity().profileType
            ? (ProfileTypeUtils.getName(entity().profileType!) | translate)
            : ""
        }}
      </p>
      <p>
        {{ "Looking for" | translate }}:
        {{
          entity().lookingFor
            ? (ProfileLookingForUtils.getName(entity().lookingFor!) | translate)
            : ""
        }}
      </p>
    </div>
  `,
})
export class StudentTopPanelComponent {
  private readonly modal = inject(ModalService);

  entity = input.required<StudentProfile>();
  profileChanged = output<void>();

  readonly ProfileTypeUtils = ProfileTypeUtils;
  readonly ProfileLookingForUtils = ProfileLookingForUtils;
  faEdit = faEdit;

  editStatus() {
    this.modal
      .Modal(ModalStudentProfileStatusComponent, "Status", "", this.entity())
      .then(() => this.profileChanged.emit())
      .catch(() => {});
  }
}

@Component({
  selector: "app-student-top-panel-actions",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, FontAwesomeModule, RouterModule],
  template: `
    <div class="flex flex-wrap justify-end gap-3 mt-3">
      <button
        class="btn btn-outline-primary"
        type="button"
        [disabled]="isSkilldLoading() || !entity().id"
        (click)="transferToSkilld()"
      >
        {{ "Transfer profile to Skilld" | translate }}
      </button>
      <button class="btn btn-primary" (click)="openAnnouncementModal()">
        <fa-icon [icon]="faBullhorn"></fa-icon>
        {{ "Place announcement" | translate }}
      </button>
      <button class="btn btn-primary" routerLink="/student/resume">
        <fa-icon [icon]="faFileLines"></fa-icon>
        {{ (isDNAEnabled ? "Generate D.N.AI CV" : "View Resume") | translate }}
      </button>
    </div>
  `,
})
export class StudentTopPanelActionsComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly alertService = inject(AlertService);
  private readonly skilldService = inject(SkilldService);
  private readonly announcementService = inject(StudentAnnouncementService);

  entity = input.required<StudentProfile>();
  profileChanged = output<void>();

  readonly isSkilldLoading = signal(false);
  readonly isDNAEnabled = APP_CONFIG.isDNAEnabled;
  faBullhorn = faBullhorn;
  faFileLines = faFileLines;

  transferToSkilld() {
    if (!this.entity().id) {
      this.alertService.error("Student profile is missing.");
      return;
    }

    this.isSkilldLoading.set(true);
    this.skilldService
      .studentprofileIdSkilldGenerateUrlGet$Response({ id: this.entity().id! })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp) => {
          const location =
            resp.headers.get("location") || resp.headers.get("Location");
          if (resp.status >= 300 && resp.status < 400 && location) {
            window.location.assign(location);
            return;
          }
          this.alertService.error("Could not redirect to Skilld.");
          this.isSkilldLoading.set(false);
        },
        error: () => {
          this.alertService.error("Could not redirect to Skilld.");
          this.isSkilldLoading.set(false);
        },
      });
  }

  openAnnouncementModal(): void {
    this.announcementService.open(this.entity());
  }
}
